/**
 * One month's bill (concept C): header, the allowance meter (current month,
 * piece-price plans only), the Charge | Qty | Price | Amount table, the
 * totals, and the card on file.
 *
 * The table is the 3PL bill's (`ThreePlBill.tsx`): one value per cell, the
 * shared `MoneyCell` / `CountCell`, a bold title with grey sub-lines, a
 * `<tfoot>` total on `bg-muted/30`. Every amount is in the bill's own
 * currency, ex GST unless the row says otherwise (L7).
 *
 * - Current month: the backend's estimate as the three locked rows (fee in
 *   advance, included pieces, extra pieces). A plan with no piece price shows
 *   the fee alone, the piece count as a plain fact, and one sentence saying
 *   pieces are for the record; never an allowance or an invented overage.
 * - Past month: every Stripe invoice line, one for one (a sign-up invoice can
 *   carry one-off lines), with Stripe's own description for an `other` line.
 * - GST is Stripe's, never ours: a GST row and a Total inc GST only when
 *   Stripe has a figure for this subtotal, else one grey line.
 */
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { CountCell, MoneyCell } from '@/components/shared/NumberCells';
import { NoteLine } from '@/components/shared/NoteLine';
import { cn } from '@/lib/utils';
import { useBillingFormat } from '../hooks/useBillingFormat';
import type { BillingPortal } from '../hooks/useBillingPortal';
import { cardBrandLabel } from '../lib/billing-format';
import {
  CARD_CLASS,
  CHARGE_TD,
  DETAIL_P,
  NUMBER_TD,
  NUMBER_TH,
  PRIMARY_ACTION_CLASS,
  SECONDARY_ACTION_CLASS,
  TEXT_TH,
} from '../lib/billing-ui';
import {
  currentBill,
  isRealPeriod,
  pastBill,
  pieceMeter,
  type ActiveOverview,
  type BillRow,
  type MonthEntry,
} from '../lib/billing-view';
import { BillingStatusPill } from './BillingStatusPill';

interface BillingBillCardProps {
  overview: ActiveOverview;
  entry: MonthEntry;
  portal: BillingPortal;
  /** Pay now opened Stripe's invoice page: the tab re-reads Stripe when the customer comes back. */
  onPayNow?: () => void;
}

const COLUMN_COUNT = 4;

export function BillingBillCard({ overview, entry, portal, onPayNow }: BillingBillCardProps) {
  const { t } = useTranslation('billing');
  const fmt = useBillingFormat();
  const titleId = useId();
  const isCurrent = entry.kind === 'current';
  const currency = entry.currency;
  const model = isCurrent ? currentBill(overview) : pastBill(entry.month);
  const meter = isCurrent ? pieceMeter(overview) : null;
  const included = overview.plan.includedPieces;
  const money = (cents: number) => fmt.money(cents, currency);
  const unitPrice = (cents: number) => fmt.unitPrice(cents, currency);
  const { card } = overview;

  // ── Header ────────────────────────────────────────────────────────────────
  let title: string;
  let sub: string;
  let actions: ReactNode = null;
  if (entry.kind === 'current') {
    title = t('bill.next', { date: fmt.day(overview.currentPeriod.nextInvoiceDate) });
    const charged = card
      ? t('bill.chargedTo', { brand: cardBrandLabel(card.brand), last4: card.last4 })
      : t('bill.chargedToSaved');
    if (overview.hasPiecePrice) {
      const addingUp = t('bill.addingUp', { date: fmt.day(overview.currentPeriod.end) });
      sub = [charged, addingUp].join(' ');
    } else {
      sub = charged;
    }
  } else {
    const { month } = entry;
    title = month.number
      ? t('bill.invoice', { number: month.number, date: fmt.day(month.date) })
      : t('bill.invoiceNoNumber', { date: fmt.day(month.date) });
    const due = t('money.incGst', { amount: money(month.amountRemainingCents) });
    if (entry.status === 'paid' && month.paidAt) {
      // The card on file now: Stripe's invoice does not carry the paying card
      // without an extra expand, and a past card is almost always the same one.
      sub = card
        ? t('bill.sub.paidOn', {
            date: fmt.day(month.paidAt),
            brand: cardBrandLabel(card.brand),
            last4: card.last4,
          })
        : t('bill.sub.paidOnNoCard', { date: fmt.day(month.paidAt) });
    } else {
      sub = t(`bill.sub.${entry.status}`, { amount: due });
    }
    // The declined notice already offers Pay now for its invoice; any other
    // unpaid invoice gets its own here.
    const payable =
      (entry.status === 'due' || entry.status === 'payment_failed') &&
      month.hostedInvoiceUrl !== null &&
      month.hostedInvoiceUrl !== overview.failedInvoice?.hostedUrl;
    actions = (
      <div className="flex flex-wrap gap-2">
        {payable && month.hostedInvoiceUrl && (
          <Button asChild className={PRIMARY_ACTION_CLASS}>
            <a href={month.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" onClick={onPayNow}>
              {t('payNow')}
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">{t('opensStripe')}</span>
            </a>
          </Button>
        )}
        {month.invoicePdfUrl && (
          <Button asChild variant="outline" className={SECONDARY_ACTION_CLASS}>
            <a
              href={month.invoicePdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('bill.pdfAria', { number: month.number ?? '' })}
            >
              <FileText className="h-3.5 w-3.5" aria-hidden="true" />
              {t('bill.pdf')}
            </a>
          </Button>
        )}
      </div>
    );
  }

  // ── Rows ──────────────────────────────────────────────────────────────────
  const rowTitle = (row: BillRow): string => {
    switch (row.kind) {
      case 'fee':
        return t('bill.row.fee');
      case 'included_pieces':
        return t('bill.row.included');
      case 'extra_pieces':
        return t('bill.row.extra');
      case 'other':
        return row.description ?? t('bill.row.other');
    }
  };
  const rowSub = (row: BillRow): string | null => {
    if (!isRealPeriod(row.period)) return null;
    const period = fmt.period(row.period);
    switch (row.kind) {
      case 'fee':
        return t('bill.row.feeSub', { period });
      case 'extra_pieces':
        return included === null ? period : t('bill.row.extraSub', { included, period });
      default:
        return period;
    }
  };

  const hasPieceLines = model.rows.some((row) => row.kind === 'included_pieces' || row.kind === 'extra_pieces');
  const notes: string[] = [];
  if (model.totals.gstOnInvoice) notes.push(t('bill.gstOnInvoice'));
  if (!overview.hasPiecePrice && !hasPieceLines) notes.push(t('bill.setPrice'));
  const gap = entry.kind === 'invoice' ? entry.month.pieceCountGap : null;
  if (gap !== null && gap > 0) notes.push(t('bill.countedAfter', { count: gap }));
  if (gap !== null && gap < 0) notes.push(t('bill.stripeBilledMore', { count: -gap }));

  const totalLabel = isCurrent ? t('bill.totalSoFar') : t('bill.total');

  return (
    <section aria-labelledby={titleId} className={CARD_CLASS}>
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-b px-3 py-3 sm:px-4">
        <div className="min-w-0 flex-1 basis-64 space-y-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 id={titleId} className="text-base font-semibold tabular-nums">
              {title}
            </h3>
            <BillingStatusPill status={entry.status} />
          </div>
          <p className="max-w-prose text-sm text-muted-foreground">{sub}</p>
        </div>
        {actions}
      </header>

      {isCurrent && (
        <div className="space-y-2 border-b px-3 py-3 sm:px-4">
          {meter ? (
            <>
              <Progress
                value={meter.percent}
                role="img"
                aria-label={t('meter.aria', {
                  pieces: t('count.pieces', { count: meter.used }),
                  included: meter.includedUsed,
                  extra: meter.extra,
                })}
                className="h-2"
                indicatorClassName={meter.over ? 'bg-primary-strong' : 'bg-threadheads-purple'}
              />
              <p className="text-sm tabular-nums" aria-hidden="true">
                {t('meter.summary', {
                  pieces: t('count.pieces', { count: meter.used }),
                  included: meter.included,
                  extra: meter.extra,
                })}
              </p>
            </>
          ) : (
            <p className="text-sm tabular-nums">
              {t('meter.soFar', { pieces: t('count.pieces', { count: overview.pieces.used }) })}
            </p>
          )}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b">
              <th scope="col" className={TEXT_TH}>
                {t('bill.col.charge')}
              </th>
              <th scope="col" className={cn(NUMBER_TH, 'sm:w-20')}>
                {t('bill.col.qty')}
              </th>
              <th scope="col" className={cn(NUMBER_TH, 'sm:w-24')}>
                {t('bill.col.price')}
              </th>
              <th scope="col" className={cn(NUMBER_TH, 'sm:w-28')}>
                {t('bill.col.amount')}
              </th>
            </tr>
          </thead>
          <tbody>
            {model.rows.length === 0 ? (
              <tr className="border-b">
                <td colSpan={COLUMN_COUNT} className="px-3 py-6 text-center text-sm text-muted-foreground sm:px-4">
                  {t('bill.empty')}
                </td>
              </tr>
            ) : (
              model.rows.map((row) => {
                const rowDetail = rowSub(row);
                return (
                  <tr key={row.key} className="border-b">
                    <td className={CHARGE_TD}>
                      <span className="font-semibold">{rowTitle(row)}</span>
                      {rowDetail && <p className={cn(DETAIL_P, 'tabular-nums')}>{rowDetail}</p>}
                    </td>
                    <CountCell value={row.qty ?? 0} strong className={NUMBER_TD} />
                    <MoneyCell value={row.unitCents} formatValue={unitPrice} showZero className={NUMBER_TD} />
                    <MoneyCell
                      value={row.amountCents}
                      formatValue={money}
                      showZero={row.freeByDesign}
                      className={NUMBER_TD}
                    />
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot>
            <tr className="bg-muted/30">
              <th scope="row" colSpan={COLUMN_COUNT - 1} className="px-3 py-2.5 text-left font-semibold sm:px-4">
                {totalLabel}
              </th>
              <MoneyCell
                value={model.totals.exGstCents}
                formatValue={money}
                showZero
                className="px-2 py-2.5 text-right font-bold sm:px-4"
              />
            </tr>
            {model.totals.gst && (
              <>
                <tr className="border-t">
                  <th
                    scope="row"
                    colSpan={COLUMN_COUNT - 1}
                    className="px-3 py-2 text-left font-normal text-muted-foreground sm:px-4"
                  >
                    {t('bill.gst')}
                  </th>
                  <MoneyCell
                    value={model.totals.gst.gstCents}
                    formatValue={money}
                    className="px-2 py-2 text-right text-muted-foreground sm:px-4"
                  />
                </tr>
                <tr className="border-t">
                  <th scope="row" colSpan={COLUMN_COUNT - 1} className="px-3 py-2 text-left font-medium sm:px-4">
                    {t('bill.totalIncGst')}
                  </th>
                  <MoneyCell
                    value={model.totals.gst.totalCents}
                    formatValue={money}
                    strong
                    className="px-2 py-2 text-right sm:px-4"
                  />
                </tr>
              </>
            )}
          </tfoot>
        </table>
      </div>

      {notes.length > 0 && (
        <div className="space-y-1 border-t px-3 py-2.5 sm:px-4">
          {notes.map((note) => (
            <NoteLine key={note} as="p" className="max-w-prose">
              {note}
            </NoteLine>
          ))}
        </div>
      )}

      <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t px-3 py-3 sm:px-4">
        <div className="min-w-0 space-y-0.5">
          {card && (
            <p className="flex items-center gap-1.5 text-sm">
              <CreditCard className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              {t('card.onFile', { brand: cardBrandLabel(card.brand), last4: card.last4 })}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{t('card.emails')}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          className={SECONDARY_ACTION_CLASS}
          onClick={portal.open}
          disabled={portal.busy}
          aria-busy={portal.busy || undefined}
        >
          {portal.busy && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
          {t('changeCard')}
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="sr-only">{t('opensStripe')}</span>
        </Button>
      </footer>
    </section>
  );
}
